import { z } from 'zod';
import { pagedSchema } from '@/shared/reference/wire';
const text = z.string().nullish();
export const legalPersonOptionSchema = z.looseObject({
  id: z.string(),
  name: z.string().meta({ pii: 'name' }),
  registrationNumber: text.meta({ pii: 'document' }),
  tenantDisplay: text,
});
export const legalPersonOptionPageSchema = pagedSchema(legalPersonOptionSchema);
export const personOptionSchema = z.looseObject({
  id: z.string(),
  name: z.string().meta({ pii: 'name' }),
  discriminator: text,
  registrationNumber: text.meta({ pii: 'document' }),
  tenantDisplay: text,
  alternativeName: text.meta({ pii: 'name' }),
});
export const personOptionPageSchema = pagedSchema(personOptionSchema);

// ─── Listas (`GetListApiResponse<NaturalPersonReadModel | LegalPersonReadModel>`, inventário §2.4) ───

// High | Intermediary | Low (`RISK_OPTIONS`, RiskRatingBlock.tsx:19-23).
const riskRating = text;

/** Linha de `GET /NaturalPerson` (`naturalPersonReadModel.ts:36-113`), só o que a lista lê, frouxa no resto. */
export const naturalPersonListItemSchema = z.looseObject({
  id: z.string(),
  name: text.meta({ pii: 'name' }),
  registrationNumber: text.meta({ pii: 'document' }),
  email: text.meta({ pii: 'email' }),
  birthDate: text.meta({ pii: 'birthDate' }),
  riskRating,
  createdAt: text,
  tenant: text,
  tenantDisplay: text,
});
export const naturalPersonListPageSchema = pagedSchema(naturalPersonListItemSchema);
export type NaturalPersonListItem = z.output<typeof naturalPersonListItemSchema>;
export type NaturalPersonListPage = z.output<typeof naturalPersonListPageSchema>;

/** Linha de `GET /LegalPerson` (`legalPersonReadModel.ts`). */
export const legalPersonListItemSchema = z.looseObject({
  id: z.string(),
  name: text.meta({ pii: 'name' }),
  companyName: text.meta({ pii: 'name' }),
  registrationNumber: text.meta({ pii: 'document' }),
  email: text.meta({ pii: 'email' }),
  riskRating,
  createdAt: text,
  tenant: text,
  tenantDisplay: text,
});
export const legalPersonListPageSchema = pagedSchema(legalPersonListItemSchema);
export type LegalPersonListItem = z.output<typeof legalPersonListItemSchema>;
export type LegalPersonListPage = z.output<typeof legalPersonListPageSchema>;

// ─── Registro (`GET /NaturalPerson/{id}`, `GET /LegalPerson/{id}`) ──────────────────────────────
// Frouxos de propósito: o formulário carrega o registro inteiro e o `toDataModel` do legado devolve
// ao fio as chaves do modelo que vieram no GET (inventário §3.2). Aqui só o que a tela lê.

const actor = z.looseObject({ userId: text, userIdDisplay: text.meta({ pii: 'name' }) }).nullish();
const loose = z.looseObject({});

export const personAddressSchema = z
  .looseObject({
    addressName: text.meta({ pii: 'address' }),
    zipCode: text,
    city: text,
    uf: text,
    ufDisplay: text,
    district: text,
    number: z.union([z.string(), z.number()]).nullish(),
    complement: text,
  })
  .nullish();

const recordBase = {
  id: z.string(),
  registrationNumber: text.meta({ pii: 'document' }),
  name: text.meta({ pii: 'name' }),
  email: text.meta({ pii: 'email' }),
  phone: text.meta({ pii: 'phone' }),
  address: personAddressSchema,
  riskRating: text,
  createdAt: text,
  createdBy: actor,
  updatedAt: text,
  tenant: text,
  tenantDisplay: text,
  personCreditStatus: z.looseObject({ status: text }).nullish(),
  uploads: z.array(loose).nullish(),
  bankAccounts: z.array(loose).nullish(),
};

export const naturalPersonSchema = z.looseObject({ ...recordBase, birthDate: text.meta({ pii: 'birthDate' }), workplace: text, workplaceDisplay: text.meta({ pii: 'name' }) });
export type NaturalPerson = z.output<typeof naturalPersonSchema>;

export const legalPersonSchema = z.looseObject({ ...recordBase, companyName: text.meta({ pii: 'name' }), openDate: text, relatedPerson: z.array(loose).nullish() });
export type LegalPerson = z.output<typeof legalPersonSchema>;

/** `POST` devolve o id (`returnValue=Id`, `useNaturalPersonMutation`); o `PUT`, o registro ou nada. */
export const personSaveResultSchema = z.union([z.string(), z.looseObject({ id: z.string().nullish() }), z.null(), z.undefined()]);
export type PersonSaveResult = z.output<typeof personSaveResultSchema>;

/** `POST /{Pessoa}/{id}/BankAccount` (personService.ts:52): a lista de ids das contas criadas. */
export const bankAccountIdsSchema = z.array(z.string()).nullish();

/** `GET /EconomicActivityCode[/busca]` (economicActivityCode.tsx:24-48): lista pura. */
export const economicActivityCodeSchema = z.looseObject({ id: z.string(), code: text, description: text });
export const economicActivityCodeListSchema = z.array(economicActivityCodeSchema);
export type EconomicActivityCode = z.output<typeof economicActivityCodeSchema>;

// ─── Datasets de bureau (`GET /{Pessoa}/{id}/Dataset/{nome}`, services/datasets/datasets.ts:15-26) ───

export const datasetItemSchema = z.looseObject({ dataSet: z.string(), status: text, result: z.unknown(), createdAt: text, updatedAt: text });
export const datasetListSchema = z.array(datasetItemSchema);
export type DatasetItem = z.output<typeof datasetItemSchema>;

/** Item sem `dataSet` garantido: tokens de averbação (`…/dataset/ReserveToken`). */
export const queryItemSchema = z.looseObject({ status: text, result: z.unknown(), createdAt: text, updatedAt: text });
export const queryListSchema = z.array(queryItemSchema);
export type QueryItem = z.output<typeof queryItemSchema>;

// ─── Ações do registro (unidade 6A) ─────────────────────────────────────────

/** Endereço do bureau (`AddressesQueryBasicData`, personContext.tsx:245-254). */
export const basicDataAddressSchema = z.looseObject({
  addressName: text.meta({ pii: 'address' }),
  zipCode: text,
  city: text,
  uf: text,
  ufDisplay: text,
  district: text,
  number: z.union([z.string(), z.number()]).nullish(),
  complement: text,
});
export type BasicDataAddress = z.output<typeof basicDataAddressSchema>;

/**
 * `IQueryBasicData` (personContext.tsx:236-243) de `GET /Person/{documento}/QueryBasicData`: a pessoa do
 * bureau (`NaturalPersonReadModel` ou `LegalPersonReadModel`, só o que "Carregar informações" lê) e as
 * listas de telefones, endereços e e-mails. As listas aceitam `null` (o legado quebraria).
 */
export const queryBasicDataSchema = z.looseObject({
  naturalPerson: z
    .looseObject({ name: text.meta({ pii: 'name' }), birthDate: text.meta({ pii: 'birthDate' }), gender: text, mothersName: text.meta({ pii: 'name' }), nationality: text, documentType: text })
    .nullish(),
  legalPerson: z
    .looseObject({ companyName: text.meta({ pii: 'name' }), name: text.meta({ pii: 'name' }), openDate: text, initialShareCapital: z.union([z.number(), z.string()]).nullish() })
    .nullish(),
  phones: z.array(z.string().meta({ pii: 'phone' })).nullish(),
  addresses: z.array(basicDataAddressSchema).nullish(),
  emails: z.array(z.string().meta({ pii: 'email' })).nullish(),
  professionData: z.unknown().optional(),
});
export type QueryBasicData = z.output<typeof queryBasicDataSchema>;

/** `ComplianceCheckListType` (services/creditNote/complianceChecklist/types): um item da blacklist, na ordem do legado. */
export interface ComplianceChecklistCreate {
  readonly attributeType: string;
  readonly attributeValue: string | null | undefined;
  readonly recordId: string;
  readonly recordType: 'naturalperson' | 'legalperson';
}
